/**
 * NCR number helpers.
 *
 * `ncr_number` carries EITHER the customer's own reference (typed in as
 * "Company NCR/SRN No." when the NCR is raised) OR, when that was left
 * blank, the system-issued number from the `NCR#####` series the backend
 * assigns on first save. The two live in one column, so a blank company
 * reference comes back looking like "NCR00001" — and the "Company NCR/SRN
 * No." export column / detail field showed the system number as if the
 * customer had entered it. The backend identifies its own series with the
 * same `^NCR\d+$` pattern used here.
 */

/** True when the number is from the system-issued `NCR#####` series. */
export function isSystemNcrNumber(ncrNumber: string | null | undefined): boolean {
  return /^NCR\d+$/i.test((ncrNumber ?? '').trim());
}

/**
 * The customer's own reference, or '' when none was given (i.e. the stored
 * number is the system-issued one).
 *
 * @param ncrNumber - the NCR's stored `ncr_number`
 * @returns the company reference verbatim, or '' for a system number
 */
export function companyNcrReference(ncrNumber: string | null | undefined): string {
  const value = (ncrNumber ?? '').trim();
  return isSystemNcrNumber(value) ? '' : value;
}

/** "minor" -> "Minor" for exports when the API sends no display label. */
export function capitaliseSeverity(severity: string | null | undefined): string {
  const value = (severity ?? '').trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}
